import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Empty, Table, TableProps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

// services
import NotificationService from 'core/services/notification';
import { onDeleteLanguage, onGetLanguages } from 'modules/languages/services/languages';

// models
import { Language } from 'modules/languages/models/language';

// components
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import AuthActionComponent from 'components/authAction/authAction';
import Icon from 'utils/icon';
import NoData from 'components/noData/noData';
import { useTranslation } from 'react-i18next';

function ListLanguagesPage() {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [languages, setLanguages] = useState<Language[]>([]);
	const [isDeleting, setIsDeleting] = useState(false);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [tempDeletedLanguage, setTempDeletedLanguage] = useState<number>();

	// pagination states
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [isLoadMore, setIsLoadMore] = useState(false);

	const columns: TableProps<Language>['columns'] = [
		{
			title: t('LANGUAGES.NAME'),
			dataIndex: 'name',
			key: 'name',
			render: (name, language) => (
				<p className="mb-0 fw-medium">
					{name} {language.default && <span className="status default ms-1">{t('LANGUAGES.DEFAULT')}</span>}
				</p>
			),
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'active',
			key: 'active',
			render: (active) => <span className={`status fw-medium ${active ? 'active' : 'inactive'}`}>{t(active ? 'COMMON.ACTIVE' : 'COMMON.INACTIVE')}</span>,
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: '',
			key: '',
			render: (_, language) => (
				<div className="d-flex gap-2">
					<AuthActionComponent action="language_update">
						<Link to={`${language.id}`}>
							<Icon name="edit" width={35} height={35} />
						</Link>
					</AuthActionComponent>
					<AuthActionComponent action="language_delete">
						<Button type="link" className="p-0" onClick={() => onShowConfirmation(language.id)}>
							<Icon name="delete" width={35} height={35} />
						</Button>
					</AuthActionComponent>
				</div>
			),
		},
	];

	useEffect(() => {
		getLanguages();
	}, []);

	const getLanguages = async () => {
		try {
			setIsLoading(true);

			const response = await onGetLanguages();
			setLanguages(response.data.data.languages);
			setTotalRecords(response.data.data.pagination.total);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			setIsLoadMore(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });
			const response = await onGetLanguages(page);
			setLanguages(response.data.data.languages);
			setCurrentPage(page);
			setIsLoadMore(false);
		} catch (error) {
			setIsLoadMore(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const onShowConfirmation = (languageId: number) => {
		setShowConfirmation(true);
		setTempDeletedLanguage(languageId);
	};

	const onDiscardConfirmation = () => {
		setShowConfirmation(false);
	};

	const onDelete = async () => {
		try {
			setIsDeleting(true);
			await onDeleteLanguage(tempDeletedLanguage as number);
			NotificationService.success(t('COMMON.SUCCESS'), t('LANGUAGES.DELETED_SUCCESS'));
			setShowConfirmation(false);
			setIsDeleting(false);
			getLanguages();
		} catch (error) {
			setIsDeleting(false);
			setShowConfirmation(false);
			NotificationService.error(t('COMMON.ERROR'), t('LANGUAGES.DELETE_ERROR'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<h3>{t('LANGUAGES.MANAGEMENT_TITLE')}</h3>
			</div>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<div className="sectionContainer listData">
					<header>
						<div>
							<h5>
								{totalRecords} {t('LANGUAGES.MANAGEMENT_COUNT')}
							</h5>
							<p>{t('LANGUAGES.MANAGEMENT_DESCRIPTION')}</p>
						</div>
						<AuthActionComponent action="language_create">
							<Link to="create">
								<Button type="primary" icon={<PlusOutlined />} style={{ background: '#F1F1F2', color: '#7E8299', border: 'none' }}>
									{t('COMMON.ADD')}
								</Button>
							</Link>
						</AuthActionComponent>
					</header>

					<div style={{ position: 'relative' }}>
						<Table
							columns={columns}
							dataSource={languages}
							scroll={{ x: 600 }}
							rowKey="id"
							pagination={{
								pageSize: 15,
								total: totalRecords,
								showSizeChanger: false,
								current: currentPage,
								onChange: fetchMoreData,
							}}
							locale={{ emptyText: <NoData /> }}
						/>

						{isLoadMore && (
							<div className="loadMoreContainer">
								<LoadingComponent />
							</div>
						)}
					</div>
				</div>
			)}

			<ConfirmModalComponent
				open={showConfirmation}
				confirmText={t('COMMON.DELETE')}
				onDiscard={onDiscardConfirmation}
				title={t('LANGUAGES.DELETE_TITLE')}
				description={t('LANGUAGES.DELETE_DESCRIPTION')}
				note={t('LANGUAGES.DELETE_NOTE')}
				onConfirm={onDelete}
				isLoading={isDeleting}
			/>
		</>
	);
}

export default ListLanguagesPage;
